import React, { useEffect, useState } from 'react';
import { progressAPI } from '../services/api';
import styles from '../styles/CelebrationBubble.module.css';

// One benchmarks fetch per session, shared by every bubble instance (history
// can render several notes). The bars show the user's CURRENT standing — the
// point is visibility of the comparison, not a frozen snapshot.
let benchmarksPromise = null;
function fetchBenchmarksOnce() {
  if (!benchmarksPromise) {
    benchmarksPromise = progressAPI.getBenchmarks().catch(err => {
      benchmarksPromise = null; // allow a retry on the next mount
      throw err;
    });
  }
  return benchmarksPromise;
}

// The four peer-comparison metrics (same set as the progress dashboard views)
const METRICS = [
  { key: 'todos', label: 'Tasks' },
  { key: 'learning', label: 'Learning' },
  { key: 'conversation', label: 'Engagement' },
  { key: 'barriers', label: 'Self-Awareness' }
];

/**
 * Centered in-chat progress note (<Celebration:{...}>) — a delivered
 * encouragement framed around the user's progress, persisted in the
 * conversation history, with the peer-comparison percentile bars from the
 * progress dashboard surfaced inline. Bead unlocks never render here: they
 * show as the BeadUnlockModal the moment they're awarded.
 */
function CelebrationBubble({ factType, message, progress, disableBenchmarks = false }) {
  const [metrics, setMetrics] = useState(null);

  // disableBenchmarks: the public org landing page renders this note inside a
  // demo, with no session — skip the (authenticated) benchmarks request.
  useEffect(() => {
    if (disableBenchmarks) return undefined;
    let mounted = true;
    fetchBenchmarksOnce()
      .then(data => {
        if (!mounted) return;
        // Realtime (days 0-13) and weekly (14+) responses carry the same
        // metric shape under different keys — normalize.
        const m = data?.benchmarkType === 'realtime'
          ? data.cohortComparison
          : data?.weeklyComparison?.metrics;
        if (m) setMetrics(m);
      })
      .catch(() => { /* bars are an enhancement — the note renders without them */ });
    return () => { mounted = false; };
  }, [disableBenchmarks]);

  if (!message) return null;

  const tasksCompleted = progress?.tasksCompleted || 0;
  const daysActive = progress?.daysActive || 0;
  const statParts = [];
  if (tasksCompleted > 0) {
    statParts.push(`${tasksCompleted} ${tasksCompleted === 1 ? 'task' : 'tasks'} completed`);
  }
  if (daysActive > 0) {
    statParts.push(`${daysActive} ${daysActive === 1 ? 'day' : 'days'} active`);
  }

  // Surface only the user's STRONGEST metric (product direction: one bar with
  // context beats four bars competing for attention).
  const best = METRICS
    .map(({ key, label }) => ({ label, data: metrics?.[key] }))
    .filter(b => b.data && typeof b.data.percentile === 'number' && b.data.cohortSize > 0)
    .sort((a, b) => b.data.percentile - a.data.percentile)[0] || null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.bubble}>
        <span className={styles.icon} aria-hidden="true">i</span>
        <div className={styles.title}>Your Progress</div>
        <div className={styles.message}>{message}</div>
        {best && (
          <div className={styles.bars}>
            <div className={styles.barRow}>
              <span className={styles.barLabel}>{best.label}</span>
              <div className={styles.barTrack}>
                <div
                  className={styles.barFill}
                  style={{ width: `${Math.max(4, Math.min(100, best.data.percentile))}%` }}
                />
              </div>
              <span className={styles.barValue}>{best.data.percentile}%</span>
            </div>
            <div className={styles.barsCaption}>
              {best.data.message || `Your strongest area right now — ahead of ${best.data.percentile}% of others at your stage`}
              {best.data.cohortSize > 5 ? ` (vs ${best.data.cohortSize} others at your stage)` : ''}
            </div>
          </div>
        )}
        {statParts.length > 0 && (
          <div className={styles.stats}>{statParts.join(' · ')}</div>
        )}
      </div>
    </div>
  );
}

export default CelebrationBubble;
